import { useContext } from 'react';
import { T, useGT } from 'gt-react';
import { AuthContext } from '@/context/auth.context';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { OrgUserSelect } from '@/components/users/OrgUserSelect';
import { useCanReportOnBehalf } from './useCanReportOnBehalf';

/**
 * "Reporting for" — myself, or another user in this organization. Renders
 * nothing for users without the permission. Cross-org filing by staff goes
 * through the org switcher first; the picker is in-org only.
 *
 * The picker reads `core.users.listInOrg`, not the members endpoint: that
 * one needs `User:Read`, which a triager (Feedback:Update) need not hold.
 */
export function ReportingForField({
  value,
  onChange,
  disabled,
}: {
  /** The selected reporter, or undefined for "myself". */
  value: string | undefined;
  onChange: (userId: string | undefined) => void;
  disabled?: boolean;
}) {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const canReportOnBehalf = useCanReportOnBehalf();
  if (!canReportOnBehalf || !currentUser) return null;

  const mode = value === undefined ? 'self' : 'other';

  return (
    <div className="flex flex-col gap-2">
      <Label>
        <T context="label above the choice of who a feedback is filed for">
          Reporting for
        </T>
      </Label>
      <RadioGroup
        value={mode}
        onValueChange={(next) => onChange(next === 'self' ? undefined : '')}
        className="flex items-center gap-4"
        disabled={disabled}
      >
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <RadioGroupItem value="self" id="reporting-for-self" />
          <T context="radio option: file the feedback for myself">Myself</T>
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <RadioGroupItem value="other" id="reporting-for-other" />
          <T context="radio option: file the feedback on behalf of another user">
            Another user
          </T>
        </label>
      </RadioGroup>
      {mode === 'other' && (
        <div className="flex flex-col gap-1">
          <OrgUserSelect
            userId={value || undefined}
            onSelect={(id) => onChange(id ?? '')}
            excludeUserIds={[currentUser.id]}
            disabled={disabled}
            placeholder={gt('Search users in this organization…', {
              context: 'placeholder for the on-behalf-of user picker',
            })}
          />
          {currentUser.isAdmin && (
            <p className="text-xs text-muted-foreground">
              <T context="helper text for staff filing a bug on behalf of a user in another organization">
                To file for a user in another organization, switch to that
                organization first.
              </T>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
